import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GoEye, GoEyeClosed } from 'react-icons/go';
import CloseIcon from '../assets/close.svg';
import SpinnerIcon from '../assets/spinner2.svg';
import api from '../api/api';
import log from '../utils/logger';
import { getApiErrorMessage } from '../utils/apiError';
import useToast from '../hooks/useToast';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import Toast from './Toast';

// State machine: email → otp → newPassword → success
const STEP = {
  EMAIL: 'email',
  OTP: 'otp',
  NEW_PASSWORD: 'newPassword',
  SUCCESS: 'success',
};

const RESEND_SECONDS = 60;
const OTP_LENGTH = 6;

const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const validatePassword = (value) =>
  value.length >= 8 &&
  /[A-Z]/.test(value) &&
  /[a-z]/.test(value) &&
  /\d/.test(value);

const ForgotPasswordModal = ({ isOpen, onClose, initialEmail = '' }) => {
  const { t, i18n } = useTranslation();
  const { toastMessage, toastType, showToast } = useToast();
  useBodyScrollLock(isOpen);

  const [step, setStep] = useState(STEP.EMAIL);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState(initialEmail || '');
  const [emailError, setEmailError] = useState('');

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const otpInputsRef = useRef([]);
  const [otpError, setOtpError] = useState('');

  const [resendCountdown, setResendCountdown] = useState(0);

  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Reset state when modal closes so a re-open starts fresh.
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setShow(true), 10);
      setStep(STEP.EMAIL);
      setEmail(initialEmail || '');
      setEmailError('');
      setOtp(Array(OTP_LENGTH).fill(''));
      setOtpError('');
      setResendCountdown(0);
      setResetToken(null);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
    } else {
      setShow(false);
    }
     
  }, [isOpen]);

  // Resend countdown tick
  useEffect(() => {
    if (resendCountdown <= 0) {
      return undefined;
    }
    const id = setTimeout(() => setResendCountdown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCountdown]);

  const handleRequest = async (e) => {
    e?.preventDefault?.();
    if (!validateEmail(email)) {
      setEmailError(t('logininvalidemail'));
      return;
    }
    setEmailError('');
    setLoading(true);
    try {
      await api.post('/user/auth/forgot-password/request', {
        email,
        language: i18n.language,
      });
      setStep(STEP.OTP);
      setResendCountdown(RESEND_SECONDS);
      setTimeout(() => otpInputsRef.current[0]?.focus(), 100);
    } catch (error) {
      log.error('forgot-password request failed', error);
      showToast(getApiErrorMessage(error, t), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value;
    if (!/^\d?$/.test(value)) {
      return;
    }
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    setOtpError('');
    if (value && index < OTP_LENGTH - 1) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) {
      return;
    }
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((c, i) => {
      next[i] = c;
    });
    setOtp(next);
    otpInputsRef.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleVerify = async (e) => {
    e?.preventDefault?.();
    const code = otp.join('');
    if (code.length !== OTP_LENGTH) {
      setOtpError(t('forgotPasswordOtpIncomplete', { defaultValue: 'Enter the 6-digit code.' }));
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/user/auth/forgot-password/verify', {
        email,
        otp: code,
      });
      const token = response.data?.response?.data?.resetToken;
      if (!token) {
        throw new Error('Missing reset token in response');
      }
      setResetToken(token);
      setStep(STEP.NEW_PASSWORD);
    } catch (error) {
      log.error('forgot-password verify failed', error);
      setOtpError(getApiErrorMessage(error, t));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCountdown > 0 || loading) {
      return;
    }
    setLoading(true);
    try {
      await api.post('/user/auth/forgot-password/resend', {
        email,
        language: i18n.language,
      });
      setResendCountdown(RESEND_SECONDS);
      setOtp(Array(OTP_LENGTH).fill(''));
      setOtpError('');
      otpInputsRef.current[0]?.focus();
      showToast(
        t('forgotPasswordCodeResent', { defaultValue: 'New code sent.' }),
        'success',
      );
    } catch (error) {
      log.error('forgot-password resend failed', error);
      showToast(getApiErrorMessage(error, t), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e?.preventDefault?.();
    if (!validatePassword(newPassword)) {
      setPasswordError(
        t('forgotPasswordWeak', {
          defaultValue:
            'Password must be at least 8 characters with one uppercase, one lowercase, and one number.',
        }),
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(
        t('forgotPasswordMismatch', {
          defaultValue: 'Passwords do not match.',
        }),
      );
      return;
    }
    setPasswordError('');
    setLoading(true);
    try {
      await api.post('/user/auth/forgot-password/reset', {
        resetToken,
        newPassword,
      });
      setStep(STEP.SUCCESS);
    } catch (error) {
      log.error('forgot-password reset failed', error);
      setPasswordError(getApiErrorMessage(error, t));
    } finally {
      setLoading(false);
    }
  };

  const renderEmailStep = () => (
    <form onSubmit={handleRequest}>
      <h1 className="text-2xl font-semibold mb-2">{t('forgotpassword')}</h1>
      <p className="text-sm text-[#6d717b] mb-6">
        {t('forgotPasswordIntro', {
          defaultValue:
            'Enter the email associated with your account and we will send you a 6-digit code.',
        })}
      </p>
      <label htmlFor="forgot-email" className="text-sm font-medium block mb-2">
        {t('email')}
      </label>
      <input
        id="forgot-email"
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t('logininputplaceholder')}
        className="rounded-[10px] min-h-[50px] px-5 mb-1 border border-[#E5E7EB] placeholder:font-light w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0"
      />
      {emailError && (
        <span className="text-xs text-red-500">{emailError}</span>
      )}
      <button
        type="submit"
        disabled={loading}
        className="h-[52px] mt-8 w-full rounded-lg bg-[#0b1223] text-white font-medium text-base flex items-center justify-center disabled:opacity-50"
      >
        {loading ? (
          <img src={SpinnerIcon} alt="" className="h-5 w-5 animate-spin" />
        ) : (
          t('forgotPasswordSendCode', { defaultValue: 'Send code' })
        )}
      </button>
    </form>
  );

  const renderOtpStep = () => (
    <form onSubmit={handleVerify}>
      <h1 className="text-2xl font-semibold mb-2">{t('forgotpassword')}</h1>
      <p className="text-sm text-[#6d717b] mb-2">
        {t('forgotPasswordOtpIntro', {
          defaultValue: 'We sent a 6-digit code to:',
        })}
      </p>
      <p className="text-sm font-semibold mb-6 break-all">{email}</p>

      <div
        className="flex items-center justify-center gap-2 mt-2"
        onPaste={handleOtpPaste}
      >
        {otp.map((value, index) => (
          <input
            key={index}
            ref={(el) => (otpInputsRef.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={value}
            onChange={(e) => handleOtpChange(e, index)}
            onKeyDown={(e) => handleOtpKeyDown(e, index)}
            className="max-h-[52px] text-center py-2 text-black text-3xl font-bold max-w-[52px] w-full border-b-2 border-black outline-none focus:border-[#1976d2] transition-all"
          />
        ))}
      </div>

      {otpError && (
        <p className="text-xs text-red-500 text-center mt-3">{otpError}</p>
      )}

      <p className="text-[#918d8d] text-sm text-right mt-8">
        {resendCountdown > 0 ? (
          <span>
            {t('resendOTP')} {resendCountdown}s
          </span>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            disabled={loading}
            className="text-black uppercase font-bold underline text-sm disabled:opacity-50"
          >
            {t('resendOTP')}
          </button>
        )}
      </p>

      <button
        type="submit"
        disabled={loading || otp.join('').length !== OTP_LENGTH}
        className="h-[52px] mt-8 w-full rounded-lg bg-[#0b1223] text-white font-medium text-base flex items-center justify-center disabled:opacity-50"
      >
        {loading ? (
          <img src={SpinnerIcon} alt="" className="h-5 w-5 animate-spin" />
        ) : (
          t('verifynow')
        )}
      </button>
    </form>
  );

  const renderNewPasswordStep = () => (
    <form onSubmit={handleReset}>
      <h1 className="text-2xl font-semibold mb-2">
        {t('forgotPasswordNewTitle', { defaultValue: 'Set a new password' })}
      </h1>
      <p className="text-sm text-[#6d717b] mb-6">
        {t('forgotPasswordNewIntro', {
          defaultValue:
            'Choose a strong password. Minimum 8 characters with one uppercase, one lowercase, and one number.',
        })}
      </p>

      <label htmlFor="new-password" className="text-sm font-medium block mb-2">
        {t('New password')}
      </label>
      <div className="flex items-center rounded-[10px] min-h-[50px] px-5 mb-3 border border-[#E5E7EB] hover:border-black focus-within:border-2 focus-within:border-[#1976d2] transition-all">
        <input
          id="new-password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="flex-1 outline-none border-none bg-transparent"
        />
        <button
          type="button"
          onClick={() => setShowPassword((p) => !p)}
          className="ml-2"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <GoEyeClosed /> : <GoEye />}
        </button>
      </div>

      <label
        htmlFor="confirm-password"
        className="text-sm font-medium block mb-2"
      >
        {t('Confirm password')}
      </label>
      <input
        id="confirm-password"
        type={showPassword ? 'text' : 'password'}
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        className="rounded-[10px] min-h-[50px] px-5 mb-1 border border-[#E5E7EB] w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0"
      />

      {passwordError && (
        <p className="text-xs text-red-500 mt-2">{passwordError}</p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="h-[52px] mt-8 w-full rounded-lg bg-[#0b1223] text-white font-medium text-base flex items-center justify-center disabled:opacity-50"
      >
        {loading ? (
          <img src={SpinnerIcon} alt="" className="h-5 w-5 animate-spin" />
        ) : (
          t('Update')
        )}
      </button>
    </form>
  );

  const renderSuccessStep = () => (
    <div className="text-center py-6">
      <h1 className="text-2xl font-semibold mb-2">
        {t('forgotPasswordSuccess', {
          defaultValue: 'Password updated',
        })}
      </h1>
      <p className="text-sm text-[#6d717b] mb-8">
        {t('forgotPasswordSuccessIntro', {
          defaultValue:
            'You can now sign in with your new password.',
        })}
      </p>
      <button
        type="button"
        onClick={onClose}
        className="h-[52px] w-full rounded-lg bg-[#0b1223] text-white font-medium text-base"
      >
        {t('log in')}
      </button>
    </div>
  );

  return (
    <>
      <div
        className={`fixed top-0 left-0 w-full h-dvh bg-[#212121] bg-opacity-[0.46] z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed bottom-0 lg:top-0 left-0 w-full lg:w-[550px] h-fit lg:h-full max-h-[85dvh] lg:max-h-full bg-white z-50 p-6 overflow-y-auto rounded-t-2xl lg:rounded-none transition-transform duration-300 ${
          show ? 'modalanimated' : 'whennotmodalclose'
        }`}
      >
        <div className="flex justify-end w-full pb-4">
          <button
            className="w-9 h-9 flex items-center justify-center rounded-full transition-colors duration-300 hover:bg-[#e7e6e6bf]"
            onClick={onClose}
            aria-label={t('Close')}
          >
            <img src={CloseIcon} alt="" />
          </button>
        </div>

        {step === STEP.EMAIL && renderEmailStep()}
        {step === STEP.OTP && renderOtpStep()}
        {step === STEP.NEW_PASSWORD && renderNewPasswordStep()}
        {step === STEP.SUCCESS && renderSuccessStep()}

        <Toast message={toastMessage} type={toastType} />
      </div>
    </>
  );
};

export default ForgotPasswordModal;
